import { useEffect, useRef } from 'react';

interface UseLiveIndicatorOptions {
  enabled?: boolean;
  indicatorColor?: string;
  indicatorSize?: number;
  showPulseAnimation?: boolean;
  liveThresholdSeconds?: number;
  onLiveStatusChange?: (status: LiveStatus) => void;
}

export interface LiveStatus {
  isLive: boolean;
  isNearEdge: boolean;
  liveEdge?: number;
}

export const useLiveIndicator = (
  containerRef: React.RefObject<HTMLElement>,
  playerRef: React.RefObject<any>,
  options: UseLiveIndicatorOptions = {}
) => {
  const observerRef = useRef<MutationObserver | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastStatusRef = useRef<{ isLive: boolean; isNearEdge: boolean } | null>(null);
  const {
    enabled = true,
    indicatorColor = '#ff0000',
    indicatorSize = 8,
    showPulseAnimation = true,
    liveThresholdSeconds = 15,
    onLiveStatusChange
  } = options;

  const getLiveStatus = (player: any): LiveStatus => {
    if (!player) {
      return { isLive: false, isNearEdge: false };
    }

    try {
      // Use stable public API to check if stream is live
      if (typeof player.isLive !== 'function') {
        return { isLive: false, isNearEdge: false };
      }

      const isLiveStream = player.isLive();
      if (!isLiveStream) return { isLive: false, isNearEdge: false };

      // Get the seek range using stable public API
      const videoElement = player.getMediaElement?.();
      if (!videoElement) return { isLive: true, isNearEdge: false };

      // Use stable public API to get seek range
      if (typeof player.seekRange !== 'function') {
        return { isLive: true, isNearEdge: false };
      }

      const seekRange = player.seekRange();
      if (!seekRange || seekRange.end === undefined) {
        return { isLive: true, isNearEdge: false };
      }

      const liveEdge = seekRange.end;
      const currentTime = videoElement.currentTime;
      const timeBehindLive = liveEdge - currentTime;

      // Consider "near live edge" if within the threshold
      return {
        isLive: true,
        isNearEdge: timeBehindLive <= liveThresholdSeconds,
        liveEdge
      };
    } catch (error) {
      console.error('Error checking live status:', error);
      return { isLive: false, isNearEdge: false };
    }
  };

  const seekToLiveEdge = () => {
    const player = playerRef.current;
    if (!player) return;

    try {
      // Use stable public API to get seek range
      if (typeof player.seekRange !== 'function') return;

      const seekRange = player.seekRange();
      if (!seekRange || seekRange.end === undefined) return;

      const liveEdge = seekRange.end;
      const videoElement = player.getMediaElement?.();
      if (videoElement) {
        videoElement.currentTime = liveEdge;
      }
    } catch (error) {
      console.error('Error seeking to live edge:', error);
    }
  };

  useEffect(() => {
    if (!containerRef.current || !enabled) {
      return;
    }

    const updateLiveIndicator = (currentTimeElement: Element, status: LiveStatus) => {
      const parent = currentTimeElement.parentElement;
      if (!parent) return;

      const existingContainer = parent.querySelector('.live-indicator-container');
      const isCurrentlyNearEdge = existingContainer?.getAttribute('data-near-edge') === 'true';

      // If status matches current state, no update needed
      if (existingContainer && status.isNearEdge === isCurrentlyNearEdge) {
        return;
      }

      // Remove existing container if present
      if (existingContainer) {
        existingContainer.remove();
      }

      // Create a container for indicator + text
      const container = document.createElement('span');
      container.className = 'live-indicator-container';
      container.setAttribute('data-near-edge', status.isNearEdge.toString());
      container.style.cssText = `
        display: inline-flex;
        align-items: center;
        width: 80px;
        ${!status.isNearEdge ? 'cursor: pointer;' : ''}
      `;

      // Create the circle indicator
      const indicator = document.createElement('span');
      indicator.className = 'live-indicator-dot';
      indicator.style.cssText = `
        display: inline-block;
        width: ${indicatorSize}px;
        height: ${indicatorSize}px;
        background-color: ${status.isNearEdge ? indicatorColor : '#888888'};
        border-radius: 50%;
        margin-right: 6px;
        ${status.isNearEdge && showPulseAnimation ? 'animation: pulse-live 2s infinite;' : ''}
      `;

      // Create text
      const liveText = document.createElement('span');
      liveText.className = 'live-indicator-text';
      liveText.textContent = status.isNearEdge ? 'LIVE' : 'GO TO LIVE';

      container.appendChild(indicator);
      container.appendChild(liveText);

      // Add click handler for "GO TO LIVE"
      if (!status.isNearEdge) {
        container.addEventListener('click', seekToLiveEdge);
      }

      // Insert container as sibling - before time when LIVE, after time when GO TO LIVE
      if (status.isNearEdge) {
        parent.insertBefore(container, currentTimeElement);
        (currentTimeElement as HTMLElement).style.display = 'none';
      } else {
        // Insert after the time element
        currentTimeElement.insertAdjacentElement('afterend', container);
        (currentTimeElement as HTMLElement).style.display = 'none';
      }
    };

    const removeLiveIndicator = (currentTimeElement: Element) => {
      const parent = currentTimeElement.parentElement;
      if (!parent) return;

      const container = parent.querySelector('.live-indicator-container');
      if (container) {
        container.remove();
        (currentTimeElement as HTMLElement).style.display = '';
      }
    };

    const checkForLiveContent = () => {
      const currentTimeElements = containerRef.current?.querySelectorAll('.shaka-current-time');
      const status = getLiveStatus(playerRef.current);

      // Call callback if status changed
      const lastStatus = lastStatusRef.current;
      if (
        onLiveStatusChange &&
        (!lastStatus || lastStatus.isLive !== status.isLive || lastStatus.isNearEdge !== status.isNearEdge)
      ) {
        onLiveStatusChange(status);
        lastStatusRef.current = { isLive: status.isLive, isNearEdge: status.isNearEdge };
      }

      currentTimeElements?.forEach((element) => {
        if (status.isLive) {
          updateLiveIndicator(element, status);
        } else {
          removeLiveIndicator(element);
        }
      });
    };

    // Initial check
    checkForLiveContent();

    // Set up interval to check periodically (since we need to check live edge position)
    intervalRef.current = setInterval(checkForLiveContent, 1000);

    // Set up mutation observer to watch for changes in the DOM
    observerRef.current = new MutationObserver((mutations) => {
      let shouldCheck = false;
      
      mutations.forEach((mutation) => {
        // Check if any nodes were added or text content changed
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          shouldCheck = true;
        }
        
        // Check if any of the added nodes contain current time elements
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            if (element.classList?.contains('shaka-current-time') || 
                element.querySelector?.('.shaka-current-time')) {
              shouldCheck = true;
            }
          }
        });
      });
      
      if (shouldCheck) {
        // Debounce the check to avoid excessive calls
        setTimeout(checkForLiveContent, 100);
      }
    });

    // Start observing
    observerRef.current.observe(containerRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
      characterDataOldValue: true
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [containerRef, enabled, indicatorColor, indicatorSize, showPulseAnimation, liveThresholdSeconds]);

  return {
    seekToLiveEdge,
    getLiveStatus: () => getLiveStatus(playerRef.current)
  };
}; 